import { useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const EDGE_ZONE = 20;          // px from left edge to start
const ANGLE_LIMIT = 30;        // degrees — reject steep vertical swipes
const SWIPE_THRESHOLD = 80;    // px horizontal distance to trigger back
const TAB_PATHS = new Set(['/', '/stats', '/accounts', '/calendar']);

export default function SwipeNavigation({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const isActive = useRef(false);
  const [progress, setProgress] = useState(0); // 0-1 for visual bar

  // Don't enable on tab pages — nothing to go back to
  const isTabPage = TAB_PATHS.has(location.pathname);

  const handleTouchStart = useCallback((e) => {
    if (isTabPage) return;

    const x = e.touches[0].clientX;
    // Only activate from left edge
    if (x > EDGE_ZONE) return;

    // Check if touch is inside a modal, more-menu, or no-swipe zone
    const target = e.target;
    if (target.closest('[data-modal]') || target.closest('[data-more-menu]') || target.closest('[data-no-swipe]')) {
      return;
    }

    touchStartX.current = x;
    touchStartY.current = e.touches[0].clientY;
    isActive.current = false; // Not yet committed
  }, [isTabPage]);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;

    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;

    // First significant movement — check angle
    if (!isActive.current) {
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 10) return; // Too small to determine direction

      const angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));
      // Reject if too vertical (> ANGLE_LIMIT degrees from horizontal)
      if (angle > ANGLE_LIMIT) {
        touchStartX.current = null;
        touchStartY.current = null;
        return;
      }
      isActive.current = true;
    }

    // Only track rightward movement
    if (dx > 0) {
      setProgress(Math.min(dx / SWIPE_THRESHOLD, 1));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null) return;

    if (progress >= 1) {
      navigate(-1);
    }

    // Reset
    touchStartX.current = null;
    touchStartY.current = null;
    isActive.current = false;
    setProgress(0);
  }, [progress, navigate]);

  return (
    <div
      className="min-h-screen"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children}

      {/* Edge progress bar — accent colored, left side */}
      {progress > 0 && (
        <div
          className="fixed left-0 top-0 w-1 z-[60] rounded-r-full xl:hidden"
          style={{
            height: `${progress * 100}%`,
            backgroundColor: 'var(--accent-bg)',
            opacity: progress * 0.8,
            transition: isActive.current ? 'none' : 'height 200ms ease, opacity 200ms ease',
          }}
        />
      )}
    </div>
  );
}
